// Cómo quiere estudiar el alumno: las preferencias, sus valores y sus combinaciones.
//
// LA REGLA QUE MANDA SOBRE TODAS LAS DEMÁS: cada preferencia se describe por LO QUE HACE,
// nunca por a quién va dirigida. No hay «modo TDAH» ni «modo dislexia». Hay «sin cuenta
// atrás» y «letra más legible».
//
// Tres motivos, y el tercero es el que más pesa: un menor no tiene por qué declarar un
// diagnóstico para usar un botón; mucho alumnado sin diagnóstico se beneficia igual; y una
// etiqueta clínica en pantalla es un dato de salud a la vista de quien pase por detrás en
// clase. `prueba_preferencias.mjs` tiene un caso que rechaza las palabras clínicas en las
// etiquetas, porque una regla que solo vive en un comentario dura hasta el próximo que
// tenga prisa.
//
// LOS DOS OPUESTOS. «Sin cuenta atrás» y «con temporizador» ayudan a públicos distintos: a
// mucho alumnado con TDAH un temporizador le ancla, y a mucho alumnado con ansiedad le
// dispara. Por eso el reloj es UNA preferencia de tres valores y no dos interruptores que
// se puedan encender a la vez y contradecirse.
//
// Vive aparte y sin React porque son datos y reglas, y las dos se comprueban.

export const CLAVE = "alpha_preferencias";

/**
 * `sistema` significa «lo que diga el sistema operativo», y es el valor de partida del
 * movimiento: quien ya lo tiene puesto en su Mac no debería tener que decirlo otra vez.
 */
export const PREFERENCIAS = [
  {
    clave: "movimiento", titulo: "Movimiento",
    dice: "Cuánto se mueve la plataforma.",
    opciones: [
      { valor: "sistema", titulo: "Como en mi dispositivo" },
      { valor: "completo", titulo: "Con animaciones" },
      { valor: "reducido", titulo: "Sin animaciones" },
    ],
    porDefecto: "sistema",
  },
  {
    clave: "texto", titulo: "Texto",
    dice: "Cómo se ve lo que hay que leer.",
    opciones: [
      { valor: "normal", titulo: "Normal" },
      { valor: "comodo", titulo: "Más espaciado" },
      { valor: "maxima", titulo: "Más espaciado y letra alternativa" },
    ],
    porDefecto: "normal",
  },
  {
    clave: "tema", titulo: "Colores",
    dice: "El contraste y la intensidad del color.",
    opciones: [
      { valor: "alpha", titulo: "Los de siempre" },
      { valor: "contraste", titulo: "Más contraste" },
      { valor: "calma", titulo: "Menos intensos" },
    ],
    porDefecto: "alpha",
  },
  {
    // EL RITMO DEL MENTOR. La velocidad de lectura de una clase de treinta no es un número, es
    // un abanico: al mismo texto, uno llega en la mitad de tiempo que otro. Un solo ritmo tiene
    // que elegir a quién deja atrás. Los tres juegos de números viven en `lib/ritmo.mjs`, con
    // sus topes y su prueba; «Como está» es exactamente el de siempre.
    clave: "ritmo", titulo: "El ritmo del mentor",
    dice: "Cuánto espera con cada mensaje en pantalla antes de seguir. Siempre puedes pararlo.",
    opciones: [
      { valor: "pausado", titulo: "Mensaje a mensaje" },
      { valor: "normal", titulo: "Como está" },
      { valor: "rafaga", titulo: "En ráfaga" },
    ],
    porDefecto: "normal",
  },
  {
    // EL AVANCE, QUE ES P7 Y ES DE ACCESIBILIDAD ANTES QUE DE GUSTO. El chat de la lección
    // avanza solo: el mentor manda el siguiente mensaje tras una pausa de lectura, y lo único
    // que el alumno podía hacer era pulsar «espera un segundo» cada vez. Para quien lee
    // despacio, para quien se distrae y vuelve, o para quien necesita releer, eso convierte
    // la lección en una carrera con un botón de freno.
    //
    // «Yo voy pasando» apaga el reloj entero: no hay pausa que corra y el mentor espera. No es
    // el ritmo pausado con otro nombre —ese sigue avanzando solo, solo que más despacio—: aquí
    // no avanza nadie hasta que el alumno lo dice.
    clave: "avance", titulo: "Quién pasa de mensaje",
    dice: "Si el mentor sigue solo tras una pausa o espera a que tú se lo digas.",
    opciones: [
      { valor: "solo", titulo: "Sigue solo" },
      { valor: "yo", titulo: "Yo voy pasando" },
    ],
    porDefecto: "solo",
  },
  {
    // LA ANTICIPACIÓN DE LOS CAMBIOS DE PANTALLA, la otra mitad de P7. Que una pantalla cambie
    // sin avisar es barato para quien lo esperaba y caro para quien no: se pierde el hilo, y
    // hay que reconstruir dónde está uno antes de poder seguir leyendo.
    //
    // No es un modal que haya que confirmar —eso sería poner una puerta en cada paso—: es un
    // aviso corto ANTES del cambio, diciendo a dónde se va, y el cambio ocurre igual.
    clave: "aviso", titulo: "Antes de cambiar de pantalla",
    dice: "Si te decimos a dónde vas antes de que la pantalla cambie.",
    opciones: [
      { valor: "directo", titulo: "Cambiar sin más" },
      { valor: "anticipado", titulo: "Avisarme antes" },
    ],
    porDefecto: "directo",
  },
  {
    clave: "reloj", titulo: "El tiempo",
    dice: "Si se ve cuánto queda.",
    opciones: [
      { valor: "normal", titulo: "Como está" },
      { valor: "sin", titulo: "Sin cuenta atrás" },
      { valor: "barra", titulo: "Con barra de avance" },
    ],
    porDefecto: "normal",
  },
  {
    clave: "sonido", titulo: "Sonido",
    dice: "Qué suena y qué no.",
    opciones: [
      { valor: "todo", titulo: "Todo" },
      { valor: "sin-juego", titulo: "Sin música ni fanfarrias" },
      { valor: "silencio", titulo: "Silencio" },
    ],
    porDefecto: "todo",
  },
  {
    // LA CALIDAD DEL MUNDO, Y ES DE ACCESIBILIDAD ANTES QUE DE GUSTO (24 de septiembre de 2026).
    // Lo pidió una clase: en los portátiles del instituto la Estrella iba a tirones, y un mundo
    // que va a tirones no es un mundo más feo, es un mundo que marea y en el que no se acierta a
    // pulsar. Por defecto se decide sola —mira la máquina y, si va mal, se aligera—; los otros
    // dos valores son para quien quiera mandar: «ligera» en un aula, «completa» en casa.
    //
    // Y SE DESCRIBE POR LO QUE HACE, como todas: no dice «modo bajo rendimiento», dice si el
    // mundo brilla o se mueve. Qué se quita exactamente está en `lib/ligereza.mjs`.
    clave: "calidad", titulo: "El mundo 3D",
    dice: "Cuánto se dibuja. Si va a tirones, con menos se mueve mejor.",
    opciones: [
      { valor: "auto", titulo: "Lo que aguante mi equipo" },
      { valor: "completa", titulo: "Con brillos" },
      { valor: "ligera", titulo: "Más suave, sin brillos" },
    ],
    porDefecto: "auto",
  },
];

export const POR_CLAVE = Object.fromEntries(PREFERENCIAS.map((p) => [p.clave, p]));

/**
 * Combinaciones con nombre neutro. UN ATAJO, NO UNA JAULA: aplican varias preferencias de
 * golpe y después cada una se puede cambiar por separado. Si un preset dejara la lista
 * bloqueada, sería un «modo» con otro nombre y volveríamos al problema de la etiqueta.
 */
export const COMBINACIONES = [
  { clave: "lectura", titulo: "Lectura tranquila",
    pone: { texto: "comodo", movimiento: "reducido", reloj: "sin" } },
  { clave: "foco", titulo: "Sin distracciones",
    pone: { movimiento: "reducido", tema: "calma", sonido: "sin-juego" } },
  { clave: "silencio", titulo: "Silencio", pone: { sonido: "silencio" } },
];

export function porDefecto() {
  return Object.fromEntries(PREFERENCIAS.map((p) => [p.clave, p.porDefecto]));
}

/** Limpia lo que venga: valores que no existen, claves inventadas y basura. */
export function saneadas(guardadas) {
  const fuera = porDefecto();
  if (!guardadas || typeof guardadas !== "object") return fuera;
  for (const p of PREFERENCIAS) {
    const v = guardadas[p.clave];
    if (p.opciones.some((o) => o.valor === v)) fuera[p.clave] = v;
  }
  return fuera;
}

export function aplica(preferencias, combinacion) {
  const c = COMBINACIONES.find((x) => x.clave === combinacion);
  return c ? saneadas({ ...preferencias, ...c.pone }) : saneadas(preferencias);
}

/** Si esa combinación está puesta entera. Sirve para marcarla, no para bloquear nada. */
/**
 * Quita un atajo: devuelve a su valor por defecto SOLO las preferencias que ese atajo pone.
 *
 * Faltaba, y se notaba: los atajos se podían encender y no apagar. Un boton que se marca y
 * ya no se desmarca no es un ajuste, es un camino de ida, y el alumno que pulsa «Silencio»
 * por probar se queda sin sonido sin saber cómo volver.
 *
 * Devuelve al DEFECTO y no al estado anterior a propósito. Guardar el estado anterior
 * significaría recordar un historial —¿de cuándo?, ¿de esta sesión o de la semana pasada?—
 * y produce sorpresas: quitas un atajo y aparece algo que tocaste hace tres días. Volver al
 * defecto es lo único que el alumno puede predecir sin haberlo pensado.
 *
 * DOS ATAJOS PUEDEN COMPARTIR UNA PREFERENCIA -«Lectura tranquila» y «Sin distracciones»
 * comparten `movimiento`- así que quitar uno puede apagar el otro. Se ve en pantalla, los
 * dos botones cambian a la vez, y es preferible a la alternativa: dejar puesto un trozo del
 * atajo que acabas de quitar.
 */
export function quita(preferencias, combinacion) {
  const c = COMBINACIONES.find((x) => x.clave === combinacion);
  if (!c) return saneadas(preferencias);
  const defectos = porDefecto();
  const fuera = { ...saneadas(preferencias) };
  for (const clave of Object.keys(c.pone)) fuera[clave] = defectos[clave];
  return saneadas(fuera);
}

export function combinacionPuesta(preferencias, combinacion) {
  const c = COMBINACIONES.find((x) => x.clave === combinacion);
  if (!c) return false;
  return Object.entries(c.pone).every(([k, v]) => preferencias[k] === v);
}

/**
 * Lo que se cuelga del `<html>` para que mande el CSS. Se omite lo que está por defecto:
 * así el atributo solo aparece cuando alguien ha pedido algo, y se ve de un vistazo en el
 * inspector qué está tocado.
 *
 * Eso incluye el movimiento en «sistema», que es su valor de partida y por tanto no escribe
 * nada. Importa que no lo escriba: lo resuelve la consulta `prefers-reduced-motion` que el
 * CSS ya tenía desde antes, y fijar aquí un valor le quitaría la última palabra al sistema
 * operativo. Aquí hubo un `if` aparte para ese caso, y sobraba —la regla del valor por
 * defecto ya lo cubría—: al saboteársele no pasaba nada, porque no hacía nada. Una línea
 * que no se puede romper es una línea que no está haciendo el trabajo que dice.
 */
export function atributos(preferencias) {
  const p = saneadas(preferencias);
  const fuera = {};
  for (const [clave, valor] of Object.entries(p)) {
    if (valor === POR_CLAVE[clave].porDefecto) continue;
    fuera[`data-${clave}`] = valor;
  }
  return fuera;
}

export function lee(almacen) {
  try {
    return saneadas(JSON.parse(almacen.getItem(CLAVE)));
  } catch {
    return porDefecto();
  }
}

export function guarda(almacen, preferencias) {
  const p = saneadas(preferencias);
  try {
    almacen.setItem(CLAVE, JSON.stringify(p));
  } catch {
    // Sin almacenamiento -ventana privada, permisos- se sigue igual: las preferencias
    // valen para esta sesión y no se guardan. Peor es no dejar cambiarlas.
  }
  return p;
}
